/** Port Consumption Database — CP / Design Overall range filter helpers. */

import { toFiniteMetric } from './port-database-consumption-cell.util';

export type OverallRangeField = 'cp_overall_mt' | 'design_overall_mt';

export interface OverallRangeBounds {
  min: number;
  max: number;
}

export function computeOverallRangeBounds(
  records: unknown[],
  field: OverallRangeField,
): OverallRangeBounds | null {
  if (!Array.isArray(records) || !records.length) {
    return null;
  }

  const values = records
    .map((record) => toFiniteMetric((record as Record<string, unknown>)?.[field]))
    .filter((value): value is number => value !== null);

  if (!values.length) {
    return null;
  }

  return {
    min: Math.min(...values),
    max: Math.max(...values),
  };
}

/** Mat-slider requires min < max; pad single-value datasets slightly for the track only. */
export function ensureSliderBounds(bounds: OverallRangeBounds): OverallRangeBounds {
  if (bounds.min < bounds.max) {
    return { ...bounds };
  }

  const pad = Math.max(0.01, Math.abs(bounds.min) * 0.05 || 1);
  return {
    min: bounds.min - pad,
    max: bounds.max + pad,
  };
}

export function parseOverallRangeValue(value: unknown): number | null {
  return toFiniteMetric(value);
}

export function formatOverallRangeDisplay(value: unknown): string {
  const metric = parseOverallRangeValue(value);
  if (metric === null) {
    return '--';
  }
  if (metric === 0) {
    return '0.00';
  }
  const sign = metric > 0 ? '+' : '-';
  return `${sign}${Math.abs(metric).toFixed(2)}`;
}

export function getOverallRangeStep(min: number, max: number): number {
  const span = Math.abs(max - min);
  if (!Number.isFinite(span) || span === 0) {
    return 0.01;
  }
  if (span <= 1) {
    return 0.01;
  }
  if (span <= 10) {
    return 0.1;
  }
  if (span <= 100) {
    return 1;
  }
  const magnitude = Math.pow(10, Math.floor(Math.log10(span)) - 2);
  return Math.max(0.01, magnitude);
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

export function normalizeOverallRangeSelection(
  min: unknown,
  max: unknown,
  sliderBounds: OverallRangeBounds,
): { min: number; max: number } {
  let normalizedMin = parseOverallRangeValue(min) ?? sliderBounds.min;
  let normalizedMax = parseOverallRangeValue(max) ?? sliderBounds.max;

  normalizedMin = clamp(normalizedMin, sliderBounds.min, sliderBounds.max);
  normalizedMax = clamp(normalizedMax, sliderBounds.min, sliderBounds.max);

  if (normalizedMin > normalizedMax) {
    return { min: normalizedMax, max: normalizedMin };
  }

  return { min: normalizedMin, max: normalizedMax };
}

export function isOverallRangeFilterActive(
  selectedMin: number | null,
  selectedMax: number | null,
  datasetBounds: OverallRangeBounds | null,
): boolean {
  if (!datasetBounds || selectedMin === null || selectedMax === null) {
    return false;
  }

  const minChanged = selectedMin > datasetBounds.min;
  const maxChanged = selectedMax < datasetBounds.max;
  return minChanged || maxChanged;
}

export function matchesOverallRangeField(
  value: unknown,
  selectedMin: number | null,
  selectedMax: number | null,
  datasetBounds: OverallRangeBounds | null,
): boolean {
  if (!isOverallRangeFilterActive(selectedMin, selectedMax, datasetBounds)) {
    return true;
  }

  const metric = parseOverallRangeValue(value);
  if (metric === null) {
    return false;
  }

  return metric >= selectedMin! && metric <= selectedMax!;
}

export function getZeroPositionPercent(bounds: OverallRangeBounds | null): number {
  if (!bounds) {
    return 50;
  }

  const span = bounds.max - bounds.min;
  if (!Number.isFinite(span) || span <= 0) {
    return 50;
  }

  const position = ((0 - bounds.min) / span) * 100;
  return Math.min(100, Math.max(0, position));
}

export function getOverallRangeEndpointClass(value: unknown): string {
  const metric = parseOverallRangeValue(value);
  if (metric === null) {
    return 'pd-overall-range-filter__endpoint--neutral';
  }
  if (metric < 0) {
    return 'pd-overall-range-filter__endpoint--negative';
  }
  if (metric > 0) {
    return 'pd-overall-range-filter__endpoint--positive';
  }
  return 'pd-overall-range-filter__endpoint--neutral';
}

export function getOverallRangeError(
  type: 'min' | 'max',
  minValue: unknown,
  maxValue: unknown,
  sliderBounds: OverallRangeBounds | null,
): string | null {
  if (!sliderBounds) {
    return null;
  }

  const minNum = parseOverallRangeValue(minValue);
  const maxNum = parseOverallRangeValue(maxValue);

  if (type === 'min') {
    if (minNum !== null) {
      if (minNum < sliderBounds.min) {
        return `Min value can be from ${formatOverallRangeDisplay(sliderBounds.min)}`;
      }
      if (minNum > sliderBounds.max) {
        return `Min value can be up to ${formatOverallRangeDisplay(sliderBounds.max)}`;
      }
    }
    if (minNum !== null && maxNum !== null && minNum > maxNum) {
      return `Min value cannot exceed Max value`;
    }
  }

  if (type === 'max') {
    if (maxNum !== null) {
      if (maxNum > sliderBounds.max) {
        return `Max value can be up to ${formatOverallRangeDisplay(sliderBounds.max)}`;
      }
      if (maxNum < sliderBounds.min) {
        return `Max value can be from ${formatOverallRangeDisplay(sliderBounds.min)}`;
      }
    }
    if (minNum !== null && maxNum !== null && maxNum < minNum) {
      return `Max value cannot be less than Min value`;
    }
  }

  return null;
}


